import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import { useSelectedBusiness } from "../../../../../platform/redux/store";
import {
  SHIFT_WINDOWS,
  getResidentAssignment,
  loadResidentAssignmentsMap,
  saveResidentAssignment,
} from "../../../../../platform/util/alerts/residentAssignment";
import {
  isAlertRoutingBackendSyncEnabled,
  pullAlertRoutingSettingsFromBackend,
  pushAlertRoutingSettingsToBackend,
} from "../../../../../platform/util/alerts/alertRoutingSync";
import { StaffStatus } from "../../../../../platform/util/utils";

const SHIFT_CODES = ["AM", "PM", "NOC"];

export default function ResidentStaffAssignmentTab({ resident, businessId }) {
  const selectedBusiness = useSelectedBusiness();
  const staffs = React.useMemo(
    () =>
      (selectedBusiness?.businessStaffs || []).filter(
        (s) => Number(s?.status) === Number(StaffStatus.ACTIVE)
      ),
    [selectedBusiness?.businessStaffs]
  );

  const staffOptions = React.useMemo(
    () =>
      staffs.map((s) => ({
        id: String(s?.id),
        label: s?.user?.username || s?.user?.email || `Staff ${s?.id}`,
      })),
    [staffs]
  );

  const [state, setState] = React.useState(() =>
    getResidentAssignment(businessId, resident?.id)
  );
  const [msg, setMsg] = React.useState("");

  React.useEffect(() => {
    setState(getResidentAssignment(businessId, resident?.id));
    setMsg("");
  }, [businessId, resident?.id]);

  React.useEffect(() => {
    let cancelled = false;
    const hydrate = async () => {
      if (!businessId || !isAlertRoutingBackendSyncEnabled()) return;
      const result = await pullAlertRoutingSettingsFromBackend(businessId);
      if (cancelled || !result?.ok) return;
      setState(getResidentAssignment(businessId, resident?.id));
    };
    hydrate();
    return () => {
      cancelled = true;
    };
  }, [businessId, resident?.id]);

  const updateShift = (shift, next) => {
    setState((prev) => ({
      ...prev,
      [shift]: { ...prev?.[shift], ...next },
    }));
    setMsg("");
  };

  const handleSave = async () => {
    saveResidentAssignment(businessId, resident?.id, state);
    if (isAlertRoutingBackendSyncEnabled() && businessId) {
      const assignments = loadResidentAssignmentsMap(businessId);
      const synced = await pushAlertRoutingSettingsToBackend({
        businessId,
        assignments,
      });
      if (synced?.ok) {
        setMsg("Resident staff assignment saved.");
      } else {
        setMsg("Assignment saved locally. Backend sync is pending.");
      }
    } else {
      setMsg("Resident staff assignment saved.");
    }
  };

  if (!resident?.id) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">Select a resident first.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.75 }}>
        Resident Staff Assignment
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
        Assign primary and backup staff by shift. Alerts resolve by:
        explicit assignee, then resident shift assignment, then on-duty role fallback.
      </Typography>

      {!!msg && <Alert severity="success" sx={{ mb: 1.25 }}>{msg}</Alert>}

      <Grid container spacing={1.25}>
        {SHIFT_CODES.map((shift) => {
          const row = state?.[shift] || { primaryStaffId: "", backupStaffIds: [] };
          return (
            <Grid item xs={12} key={shift}>
              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>
                  {SHIFT_WINDOWS?.[shift]?.label || shift}
                </Typography>

                <Grid container spacing={1.25}>
                  <Grid item xs={12} md={5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Primary staff</InputLabel>
                      <Select
                        label="Primary staff"
                        value={String(row?.primaryStaffId || "")}
                        onChange={(e) => updateShift(shift, { primaryStaffId: String(e.target.value || "") })}
                      >
                        <MenuItem value="">Unassigned</MenuItem>
                        {staffOptions.map((s) => (
                          <MenuItem key={`${shift}-p-${s.id}`} value={s.id}>
                            {s.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Backup staff (optional)</InputLabel>
                      <Select
                        multiple
                        label="Backup staff (optional)"
                        value={(row?.backupStaffIds || []).map(String)}
                        onChange={(e) => {
                          const val = e.target.value;
                          const next = Array.isArray(val)
                            ? val.map(String)
                            : String(val || "")
                              .split(",")
                              .map((x) => x.trim())
                              .filter(Boolean);
                          updateShift(shift, { backupStaffIds: next });
                        }}
                        renderValue={(selected) =>
                          (selected || [])
                            .map((id) => staffOptions.find((s) => s.id === String(id))?.label || id)
                            .join(", ")
                        }
                      >
                        {staffOptions.map((s) => (
                          <MenuItem key={`${shift}-b-${s.id}`} value={s.id}>
                            <Checkbox checked={(row?.backupStaffIds || []).map(String).includes(String(s.id))} />
                            {s.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ mt: 1.5 }}>
        <Button variant="contained" onClick={handleSave}>
          Save Assignment
        </Button>
      </Box>
    </Box>
  );
}
